<template>
  <div
    :class="{
      'osk-home-gender-page': true,
      'osk-home-gender-page_isMoreDetails': isMoreDetails,
      'osk-home-gender-page_isJournalMode': isJournalMode,
      'osk-mobile-view': isMoreDetails,
    }"
  >
    <template
      v-for="(block, index) in blocks"
      :key="index"
    >
      <template
        v-if="Array.isArray(block)"
      >
        <div class="osk-home-gender-page__wrapper">
          <div class="osk-wrapper">
            <template
              v-for="(item, idx) in block"
              :key="idx"
            >
              <template v-if="item.contentType">
                <component
                  :is="BLOCK_COMPONENTS[item.contentType]"
                  :data="item"
                  :home-banner-id="homeBannerId"
                />
              </template>
            </template>
          </div>
        </div>
      </template>

      <section
        v-else-if="block.id === 'WEBTOAPP'"
        class="osk-mb-xl osk-px-md"
      >
        <WebToAppDownloadAppMobileGender app-link="https://oskelly.onelink.me/kabM/nrfjmavl" />
      </section>

      <template v-else-if="block.contentType">
        <ClientOnly v-if="block.contentType === 'STORIES'">
          <template v-if="isDesktop">
            <component
              :is="BLOCK_COMPONENTS[block.contentType]"
              :data="block"
              :home-banner-id="homeBannerId"
            />
          </template>
        </ClientOnly>

        <component
          :is="BLOCK_COMPONENTS[block.contentType]"
          v-else
          :data="block"
          :home-banner-id="homeBannerId"
          :is-more-details="isMoreDetails"
        />
      </template>
    </template>
    <UikitMorePagesButton
      v-if="!isMoreDetails && !isFilterBlock"
      class="osk-home-gender-page__more-pages"
      :loading="loading"
      @get-next-page="handleClick"
    />

    <ConciergeModal
      v-if="!isMoreDetails && showConciergeModal && isFlexiblyButton"
      :offset="conciergeModalOffset"
    />

    <UikitModal
      v-else-if="!isMoreDetails && isFlexiblyButton && isButtonExist"
      :offset="conciergeModalOffset"
      :button-text="singleBannerContent.buttonTitle"
      :handle-click="handleButtonClick"
    />
  </div>
</template>

<script lang="ts" setup>
import { ConcreteComponent } from 'vue';

import {
  ContentType, PrimaryContentDTO, BannerDTO, TextControlViewDTO, SeoLinkGroupDto,
  PrimaryPageDTO,
} from '~/restAPI/data-contracts';

type TComponents = {
  [key in ContentType]?: ConcreteComponent | string
};

type TBlocks = (PrimaryContentDTO | PrimaryContentDTO[])[]

const BLOCK_COMPONENTS: TComponents = {
  BANNER: resolveComponent('HomeBlocksBanner'),
  COLLECTION: resolveComponent('HomeBlocksCollection'),
  H_SELECTION: resolveComponent('HomeBlocksHSelection'),
  CELEBRITY: resolveComponent('HomeBlocksCelebrity'),
  ADDITIONAL_COLLECTION: resolveComponent('HomeBlocksAdditionalCollection'),
  VERTICAL_BANNER_COLLECTION: resolveComponent('HomeBlocksVerticalCollection'),
  USERS_COLLECTION: resolveComponent('HomeBlocksUsersCollection'),
  H_SELECTION_WITH_BANNER: resolveComponent('HomeBlocksHSelectionWithBanner'),
  TEXT_CONTROL: resolveComponent('HomeBlocksTextControl'),
  AD_PRODUCT: resolveComponent('HomeBlocksAdProduct'),
  BLOG: resolveComponent('HomeBlocksBlog'),
  LIST_ITEMS: resolveComponent('HomeBlocksListItems'),
  FILTERABLE_SHELF: resolveComponent('HomeBlocksFilterableShelf'),
  SINGLE_BANNER: resolveComponent('HomeBlocksSingleBanner'),
  FOOTER_BUTTONS: resolveComponent('HomeBlocksFooterButtons'),
  SOCIAL_COLLECTION: resolveComponent('HomeBlocksSocialCollection'),
  FILTERABLE_ITEMS: resolveComponent('BusinessProductsFiltersBlock'),
  SELECTION: resolveComponent('HomeBlocksSelection'),
  STORIES: resolveComponent('BusinessStoriesBlock'),
};

const props = defineProps<{
  data: PrimaryPageDTO
  loading?: boolean
  homeBannerId?: string
  showConciergeModal?: boolean
  isMoreDetails?: boolean
}>();

const emits = defineEmits<{
  (e: 'getNextPage', value: number): void,
}>();

const { headerHeight } = useHeader();
const { isDesktop } = useUiBreakpoints();
const { isMobile } = useDevice();

const conciergeModalOffset = ref(0);

const items = computed(() => props.data.items ?? []);

const blocks = computed<TBlocks>(() => {
  const allBlocks = (items.value ?? []).filter((b) => b.contentType !== 'LINK_GROUPS_BLOCK');

  let index = 0;
  const result: TBlocks = [];

  while (index < allBlocks.length) {
    const newBlock = getBlock(index, allBlocks);

    index++;

    if (Array.isArray(newBlock) && newBlock.length === 2) {
      index++;
    }

    result.push(newBlock);
  }

  if (isMobile) {
    result.splice(3, 0, {
      id: 'WEBTOAPP',
    });
  }

  return result;
});

const singleBannerContent = computed(() => items.value.find((item) => item.contentType === 'SINGLE_BANNER')?.content as BannerDTO);
const isJournalMode = computed(() => singleBannerContent.value?.isJournalMode);
const isFlexiblyButton = computed(() => singleBannerContent.value?.isFlexiblyButton);
const isButtonExist = computed(() => singleBannerContent.value?.buttonLink && singleBannerContent.value?.buttonTitle);

const isFilterBlock = computed(() => blocks.value.some((b) => b.contentType === 'FILTERABLE_ITEMS'));

function handleClick(value: number) {
  emits('getNextPage', value);
}

async function handleButtonClick() {
  const link = genSPALink(singleBannerContent.value?.buttonLink);

  if (!link) return;

  const external = !link.startsWith('/') || link.startsWith('//');

  await navigateTo(link, { external });
}

function getBlock(index: number, allBlocks: PrimaryContentDTO[]): PrimaryContentDTO | PrimaryContentDTO[] {
  const block = allBlocks[index];

  if (block.contentType !== 'TEXT_CONTROL') {
    return block;
  }

  const textControl = block?.content as TextControlViewDTO | undefined;
  const isOnlyImage = (textControlBlock?: TextControlViewDTO) => textControlBlock?.imageUrl && !textControlBlock?.text && !textControlBlock?.title;
  const onlyImage = isOnlyImage(textControl);

  if (!onlyImage) {
    return block;
  }

  const nextTextControl = allBlocks[index + 1]?.content as TextControlViewDTO | undefined;
  const onlyImageNext = isOnlyImage(nextTextControl);

  if (onlyImage && onlyImageNext) {
    return [ block, allBlocks[index + 1] ];
  }

  return block;
}

onMounted(() => {
  const banner: HTMLElement | null = document.querySelector('.osk-home-blocks-banner')
    || document.querySelector('.osk-home-blocks-single-banner');

  if (banner) {
    conciergeModalOffset.value = banner.offsetTop + banner.offsetHeight - headerHeight.value - 80;
  }
});
</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";

.osk-home-gender-page {
  display: flex;
  flex-direction: column;

  @include media-query(lg-and-up) {
    padding-bottom: 52px;
  }

  &_isMoreDetails {
    :deep(.osk-home-blocks-single-banner__card-more-link) {
      display: none;
    }
  }

  &__more-pages {
    margin-bottom: 120px;

    @include media-query(lg-and-up) {
      margin-top: -40px;
      margin-bottom: 0;
    }
  }

  &__wrapper {
    .osk-wrapper {
      @include media-query(lg-and-up) {
        display: flex;
        justify-content: center;

        :deep(.osk-blocks-text-controls_only-image) {
          .osk-blocks-text-controls__image {
            max-height: 472px;
          }

          &:nth-child(odd) {
            margin-left: -12px;
          }

          &:nth-child(even) {
            margin-right: -12px;
          }
        }
      }
    }
  }

  &_isJournalMode {
    @include media-query(lg-and-up) {
      padding-bottom: 0;

      .osk-home-gender-page__more-pages {
        margin-bottom: 52px;
      }
    }
  }
}
</style>
